import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import Home from '@/pages/HomePage.vue'
import Customers from '@/pages/CustomersPage.vue'
import Releases from '@/pages/ReleasesPage.vue'
import Roadmap from '@/pages/RoadmapPage.vue'
import Tenant from '@/pages/TenantPage.vue'
import WorkflowEdit from '@/pages/WorkflowEditPage.vue'
import Tasks from '@/pages/TasksPage.vue'
import Addresses from '@/pages/AddressesPage.vue'
import Projects from '@/pages/ProjectsPage.vue'
import Products from '@/pages/ProductsPage.vue'
import AddressDetail from '@/pages/AddressDetailPage.vue'
import ProjectDetail from '@/pages/ProjectDetailPage.vue'
import ProductDetail from '@/pages/ProductDetailPage.vue'
import Transactions from '@/pages/TransactionsPage.vue'
import TransactionDetail from '@/pages/TransactionDetailPage.vue'
import TaskHistoryEmbed from '@/pages/TaskHistoryEmbedPage.vue'
import VatAddressValidation from '@/pages/VatAddressValidationPage.vue'
import VatTransactionValidation from '@/pages/VatTransactionValidationPage.vue'
import VatValidationView from '@/components/vat/VatValidationView.vue'
import BiHub from '@/pages/bi/BiHubPage.vue'
import TenantAdmin from '@/pages/TenantAdminPage.vue'
import EmergencyReport from '@/pages/EmergencyReportPage.vue'
import FeedbackHub from '@/pages/FeedbackHubPage.vue'
import OIDCClients from '@/pages/OIDCClientsPage.vue'
import { useAuthStore } from '@/stores/auth'
import type { TenantContext } from '@/types'
import { consumePostLoginRedirectPath, redirectToLogin } from '@/utils/auth-redirect'
import { PERMISSIONS, hasAnyPermission } from '@/utils/roles'

// Embedded VAT validation views (opened from microtech) are visible with the dedicated
// VAT permission or with full microtech data access. The standard portal VAT pages stay
// behind microtech data access only.
const EMBEDDED_VAT_VALIDATION_PERMISSIONS = [PERMISSIONS.VAT_VALIDATION_READ, PERMISSIONS.MICROTECH_DATA_READ]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'Home',
      component: Home,
      meta: { requiresAuth: true, unpadded: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/customers',
      name: 'Customers',
      component: Customers,
      meta: { requiresAuth: true, permission: PERMISSIONS.PORTAL_ADMIN },
    },
    {
      path: '/customers/:customerNo/tenants',
      name: 'Tenant',
      component: Tenant,
      props: true,
      meta: { requiresAuth: true, permission: PERMISSIONS.PORTAL_ADMIN },
    },
    {
      path: '/oidc-clients',
      name: 'OIDCClients',
      component: OIDCClients,
      meta: { requiresAuth: true, permission: PERMISSIONS.PORTAL_ADMIN },
    },
    {
      path: '/microtech-hub/addresses',
      name: 'Addresses',
      component: Addresses,
      meta: { requiresAuth: true, permission: PERMISSIONS.MICROTECH_DATA_READ },
    },
    {
      path: '/microtech-hub/addresses/:fldAdrNr',
      name: 'AddressDetail',
      component: AddressDetail,
      meta: { requiresAuth: true, permission: PERMISSIONS.MICROTECH_DATA_READ },
    },
    {
      path: '/microtech-hub/addresses/:fldAdrNr/bi',
      name: 'AddressBi',
      component: AddressDetail,
      meta: { requiresAuth: true, permission: PERMISSIONS.MICROTECH_DATA_READ },
    },
    {
      path: '/microtech-hub/projects',
      name: 'Projects',
      component: Projects,
      meta: { requiresAuth: true, permission: PERMISSIONS.MICROTECH_DATA_READ },
    },
    {
      path: '/microtech-hub/projects/:fldPrjNr',
      name: 'ProjectDetail',
      component: ProjectDetail,
      props: (route: RouteLocationNormalized) => ({
        fldPrjNr: route.params.fldPrjNr,
        isArchive: route.query.isArchive,
      }),
      meta: { requiresAuth: true, permission: PERMISSIONS.MICROTECH_DATA_READ },
    },
    {
      path: '/microtech-hub/products',
      name: 'Products',
      component: Products,
      meta: { requiresAuth: true, permission: PERMISSIONS.MICROTECH_DATA_READ },
    },
    {
      path: '/microtech-hub/products/:fldArtNr',
      name: 'ProductDetail',
      component: ProductDetail,
      props: true,
      meta: { requiresAuth: true, permission: PERMISSIONS.MICROTECH_DATA_READ },
    },
    {
      path: '/microtech-hub/products/:fldArtNr/bi',
      name: 'ProductBi',
      component: ProductDetail,
      props: true,
      meta: { requiresAuth: true, permission: PERMISSIONS.MICROTECH_DATA_READ },
    },
    {
      path: '/microtech-hub/transactions',
      name: 'Transactions',
      component: Transactions,
      meta: { requiresAuth: true, permission: PERMISSIONS.MICROTECH_DATA_READ },
    },
    {
      path: '/microtech-hub/transactions/:fldBelegNr',
      name: 'TransactionDetail',
      component: TransactionDetail,
      props: (route) => ({
        fldBelegNr: route.params.fldBelegNr,
        isArchive: route.query.isArchive,
      }),
      meta: { requiresAuth: true, permission: PERMISSIONS.MICROTECH_DATA_READ },
    },
    {
      path: '/microtech-hub/analysis',
      name: 'Evaluation',
      component: () => import('@/pages/bi/EvaluationPage.vue'),
      meta: { requiresAuth: true, permission: PERMISSIONS.PORTAL_ADMIN },
    },
    {
      path: '/microtech-hub',
      name: 'Portal',
      component: Home, // Using Home for now as a placeholder
      meta: { requiresAuth: true, permission: PERMISSIONS.MICROTECH_DATA_READ },
    },
    {
      path: '/admin',
      name: 'Administration',
      component: TenantAdmin,
      meta: { requiresAuth: true, unpadded: true, permission: PERMISSIONS.SETTINGS_READ },
    },
    {
      path: '/admin/:tab',
      name: 'AdministrationTab',
      component: TenantAdmin,
      meta: { requiresAuth: true, unpadded: true, permission: PERMISSIONS.SETTINGS_READ },
    },
    {
      path: '/account',
      name: 'Account',
      component: () => import('@/pages/AccountPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/customer-admin',
      name: 'CustomerAdministration',
      component: () => import('@/pages/CustomerAdminPage.vue'),
      meta: { requiresAuth: true, unpadded: true, permission: PERMISSIONS.CUSTOMER_SETTINGS_READ },
    },
    {
      path: '/customer-admin/:tab',
      name: 'CustomerAdministrationTab',
      component: () => import('@/pages/CustomerAdminPage.vue'),
      meta: { requiresAuth: true, unpadded: true, permission: PERMISSIONS.CUSTOMER_SETTINGS_READ },
    },
    {
      path: '/customers/:customerNo/administration/:tab?',
      name: 'CustomerAdministrationFor',
      component: () => import('@/pages/CustomerAdminPage.vue'),
      props: (route) => ({ customerNo: route.params.customerNo }),
      meta: { requiresAuth: true, unpadded: true, permission: PERMISSIONS.PORTAL_ADMIN },
    },
    {
      path: '/admin/workflow/new',
      name: 'WorkflowCreate',
      component: WorkflowEdit,
      props: { blueprintId: 'new' },
      meta: { requiresAuth: true, permission: PERMISSIONS.SETTINGS_READ },
    },
    {
      path: '/admin/workflow/:blueprintId',
      name: 'WorkflowView',
      component: WorkflowEdit,
      props: (route) => ({ blueprintId: route.params.blueprintId, viewOnly: true }),
      meta: { requiresAuth: true, permission: PERMISSIONS.SETTINGS_READ },
    },
    {
      path: '/admin/workflow/:blueprintId/edit',
      name: 'WorkflowEdit',
      component: WorkflowEdit,
      props: true,
      meta: { requiresAuth: true, permission: PERMISSIONS.SETTINGS_WRITE },
    },
    {
      path: '/releases',
      name: 'Releases',
      component: Releases,
      meta: { requiresAuth: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/roadmap',
      name: 'Roadmap',
      component: Roadmap,
      meta: { requiresAuth: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/search',
      name: 'Search',
      component: () => import('@/pages/SearchPage.vue'),
      meta: { requiresAuth: true, permission: PERMISSIONS.OPENSEARCH_READ },
    },
    {
      path: '/tasks',
      name: 'Tasks',
      component: Tasks,
      meta: { requiresAuth: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/embedded/tasks',
      name: 'EmbeddedTasks',
      component: Tasks,
      props: (route) => ({ embedded: true, tenant: route.query.tenant }),
      meta: { requiresAuth: true, embedded: true, unpadded: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/embedded/tasks/:objectID',
      name: 'TaskObjectDetail',
      component: () => import('@/pages/TaskObjectDetailPage.vue'),
      props: (route) => ({ objectID: route.params.objectID, tenant: route.query.tenant, embedded: true }),
      meta: { requiresAuth: true, embedded: true, unpadded: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/tasks/:instanceId',
      name: 'TaskDetail',
      component: () => import('@/pages/TaskDetailPage.vue'),
      props: (route) => ({ instanceId: route.params.instanceId, embedded: false }),
      meta: { requiresAuth: true, unpadded: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/tasks/:instanceId/history',
      name: 'TaskHistory',
      component: TaskHistoryEmbed,
      props: (route) => ({ instanceId: route.params.instanceId, embedded: false }),
      meta: { requiresAuth: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/embedded/tasks/i/:instanceId',
      name: 'TaskDetailEmbedded',
      component: () => import('@/pages/TaskDetailPage.vue'),
      props: (route) => ({ instanceId: route.params.instanceId, embedded: true }),
      meta: { requiresAuth: true, embedded: true, unpadded: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/embedded/tasks/i/:instanceId/history',
      name: 'TaskHistoryEmbed',
      component: TaskHistoryEmbed,
      props: (route) => ({ instanceId: route.params.instanceId, embedded: true }),
      meta: { requiresAuth: true, embedded: true, unpadded: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/vat-validation/address/:addressNr',
      name: 'VatAddressValidation',
      component: VatAddressValidation,
      props: (route) => ({
        addressNr: route.params.addressNr,
        embedded: route.query.embedded === 'true',
        tenant: route.query.tenant,
        customer: route.query.customer,
      }),
      meta: { requiresAuth: true, permission: PERMISSIONS.MICROTECH_DATA_READ },
    },
    {
      path: '/embedded/vat-validation/address/:addressNr',
      name: 'EmbeddedVatAddressValidation',
      component: VatValidationView,
      props: (route) => ({
        mode: 'address',
        addressNr: route.params.addressNr,
        embedded: true,
        tenant: route.query.tenant,
        customer: route.query.customer,
      }),
      meta: { requiresAuth: true, embedded: true, unpadded: true, permission: EMBEDDED_VAT_VALIDATION_PERMISSIONS },
    },
    {
      path: '/vat-validation/transaction/:transactionNr',
      name: 'VatTransactionValidation',
      component: VatTransactionValidation,
      props: (route) => ({
        transactionNr: route.params.transactionNr,
        embedded: route.query.embedded === 'true',
        tenant: route.query.tenant,
        customer: route.query.customer,
      }),
      meta: { requiresAuth: true, permission: PERMISSIONS.MICROTECH_DATA_READ },
    },
    {
      path: '/embedded/vat-validation/transaction/:transactionNr',
      name: 'EmbeddedVatTransactionValidation',
      component: VatValidationView,
      props: (route) => ({
        mode: 'transaction',
        transactionNr: route.params.transactionNr,
        embedded: true,
        tenant: route.query.tenant,
        customer: route.query.customer,
      }),
      meta: { requiresAuth: true, embedded: true, unpadded: true, permission: EMBEDDED_VAT_VALIDATION_PERMISSIONS },
    },
    {
      path: '/embedded/vat-validation/:id',
      name: 'EmbeddedVatValidationUnified',
      component: VatValidationView,
      props: (route) => ({
        identifier: route.params.id,
        embedded: true,
        tenant: route.query.tenant,
        customer: route.query.customer,
      }),
      meta: { requiresAuth: true, embedded: true, unpadded: true, permission: EMBEDDED_VAT_VALIDATION_PERMISSIONS },
    },
    {
      path: '/customers/:customerNo/tenants/:tenantName/bi',
      name: 'BiHub',
      component: BiHub,
      props: true,
      meta: { requiresAuth: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/customers/:customerNo/tenants/:tenantName/bi/customers',
      name: 'BiCustomerDashboard',
      component: () => import('@/pages/bi/CustomerDashboard.vue'),
      props: true,
      meta: { requiresAuth: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/customers/:customerNo/tenants/:tenantName/bi/articles',
      name: 'BiArticleDashboard',
      component: () => import('@/pages/bi/ArticleDashboard.vue'),
      props: true,
      meta: { requiresAuth: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/customers/:customerNo/tenants/:tenantName/bi/suppliers',
      name: 'BiSupplierAnalysis',
      component: () => import('@/pages/bi/SupplierAnalysis.vue'),
      props: true,
      meta: { requiresAuth: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/customers/:customerNo/tenants/:tenantName/bi/sales',
      name: 'BiSalesReports',
      component: () => import('@/pages/bi/SalesReports.vue'),
      props: true,
      meta: { requiresAuth: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/customers/:customerNo/tenants/:tenantName/bi/abc',
      name: 'BiAbcAnalysis',
      component: () => import('@/pages/bi/AbcAnalysis.vue'),
      props: true,
      meta: { requiresAuth: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/customers/:customerNo/tenants/:tenantName/bi/documents',
      name: 'BiDocumentAnalysis',
      component: () => import('@/pages/bi/DocumentAnalysis.vue'),
      props: true,
      meta: { requiresAuth: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/customers/:customerNo/tenants/:tenantName/bi/product-groups',
      name: 'BiProductGroupAnalysis',
      component: () => import('@/pages/bi/ProductGroupAnalysis.vue'),
      props: true,
      meta: { requiresAuth: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/customers/:customerNo/tenants/:tenantName/bi/article-customer',
      name: 'BiArticleCustomerAnalysis',
      component: () => import('@/pages/bi/ArticleCustomerAnalysis.vue'),
      props: true,
      meta: { requiresAuth: true, permission: PERMISSIONS.TENANT_ACCESS },
    },
    {
      path: '/emergency',
      name: 'EmergencyReport',
      component: EmergencyReport,
      meta: { requiresAuth: true },
    },
    {
      path: '/feedback',
      name: 'FeedbackHub',
      component: FeedbackHub,
      meta: { requiresAuth: true },
    },
    {
      path: '/feedback/tickets',
      name: 'Tickets',
      component: () => import('@/pages/TicketsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/feedback/tickets/:iid',
      name: 'TicketDetail',
      component: () => import('@/pages/TicketsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/onboarding',
      name: 'OnboardingLanding',
      component: () => import('@/pages/onboarding/OnboardingLandingPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/onboarding/setup',
      name: 'OnboardingWizard',
      component: () => import('@/pages/onboarding/OnboardingWizardPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      // Target of the double opt-in link mailed to a recovery contact. It is
      // public on purpose: the recipient usually has no Portal session.
      path: '/onboarding/recovery-contacts/verify',
      name: 'RecoveryContactVerify',
      component: () => import('@/pages/onboarding/RecoveryContactVerifyPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      // Öffentlicher Einstieg für ausgesperrte Administratoren. Kein Login
      // möglich, deshalb bewusst ohne Portal-Sitzung erreichbar.
      path: '/recovery',
      name: 'RecoveryRequest',
      component: () => import('@/pages/recovery/RecoveryRequestPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      // Ziel des Wiederherstellungs-Links; benötigt zusätzlich einen Code.
      path: '/recovery/redeem',
      name: 'RecoveryRedeem',
      component: () => import('@/pages/recovery/RecoveryRedeemPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      // Zweistufiger Abbruch: diese Seite zeigt nur an, was gestoppt würde.
      path: '/recovery/cancel',
      name: 'RecoveryCancel',
      component: () => import('@/pages/recovery/RecoveryCancelPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      // Ziel des Wiederherstellungs-Links, den die Wiederherstellungs-Kontakte
      // nach jeder kritischen Konfigurationsänderung erhalten. Zweistufig: diese
      // Seite zeigt nur an, was zurückgesetzt würde.
      path: '/restore',
      name: 'RestoreConfirm',
      component: () => import('@/pages/restore/RestoreConfirmPage.vue'),
      meta: { requiresAuth: false },
    },
  ],
})

router.beforeEach(async (to) => {
  const authStore = useAuthStore()

  if (to.meta.requiresAuth && !authStore.isLoggedIn) {
    try {
      await authStore.fetchUserInfo()
    } catch {
      // If fetching user info fails, redirect to login
      redirectToLogin()
      return false
    }
  }

  // Handle tenant switch via query parameter
  const rawTenant = to.query.tenant ?? to.query.tenantName
  const rawCustomer = to.query.customer ?? to.query.customerNo
  const requestedTenantName =
    typeof rawTenant === 'string' && rawTenant.trim() !== '' ? rawTenant.trim() : undefined
  const requestedCustomerNo =
    typeof rawCustomer === 'string' && rawCustomer.trim() !== '' ? rawCustomer.trim() : undefined

  if ((requestedTenantName || requestedCustomerNo) && authStore.isLoggedIn) {
    const currentTenantName = authStore.userInfo?.activeTenant?.tenantName
    const currentCustomerNo = authStore.userInfo?.activeTenant?.customerNo

    let targetTenant: TenantContext | null = null

    if (requestedCustomerNo && requestedTenantName) {
      targetTenant =
        authStore.allowedTenants.find(
          (t: TenantContext) =>
            t.customerNo === requestedCustomerNo && t.tenantName === requestedTenantName
        ) ?? null
    } else if (requestedCustomerNo) {
      const customerTenants = authStore.allowedTenants.filter(
        (t: TenantContext) => t.customerNo === requestedCustomerNo
      )
      if (customerTenants.length > 0) {
        targetTenant = customerTenants.find((t: TenantContext) => t.isDefault) ?? customerTenants[0]
      }
    } else if (requestedTenantName) {
      const matches = authStore.allowedTenants.filter(
        (t: TenantContext) => t.tenantName === requestedTenantName
      )
      if (matches.length === 1) {
        targetTenant = matches[0]
      } else if (matches.length > 1) {
        console.warn(
          'Ambiguous tenant switch requested. Multiple tenants named ' +
            requestedTenantName +
            '. Please provide customer query parameter.'
        )
      }
    }

    const cleanQuery = { ...to.query }
    delete cleanQuery.tenant
    delete cleanQuery.tenantName
    delete cleanQuery.customer
    delete cleanQuery.customerNo

    if (
      targetTenant &&
      (targetTenant.customerNo !== currentCustomerNo || targetTenant.tenantName !== currentTenantName)
    ) {
      try {
        const redirectPath = to.name
          ? router.resolve({ name: to.name, params: to.params, query: cleanQuery, hash: to.hash }).fullPath
          : router.resolve({ path: to.path, query: cleanQuery, hash: to.hash }).fullPath

        await authStore.switchTenant(targetTenant.customerNo, targetTenant.tenantName, redirectPath)
        return false
      } catch (error) {
        console.error('Failed to switch tenant via query param', error)
      }
    } else {
      // Remove invalid, ambiguous, or redundant query parameter from the URL to prevent desynchronization
      return { path: to.path, query: cleanQuery, hash: to.hash, replace: true }
    }
  }

  if (to.meta.requiresAuth) {
    const postLoginPath = consumePostLoginRedirectPath()
    if (postLoginPath && postLoginPath !== to.fullPath) {
      return postLoginPath
    }

    const requiredPermissions = to.meta.permission as string | string[] | undefined
    if (
      requiredPermissions &&
      !hasAnyPermission(
        authStore.userInfo?.user.permissions,
        Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions],
      )
    ) {
      // Avoid an infinite redirect loop: if the user also lacks permission for the Home
      // route itself (the usual redirect target), let the navigation through instead of
      // bouncing back to '/' forever.
      return to.path === '/' ? true : '/'
    }
  }

  return true
})

export default router
